/**
 * Клетки матрицы «вопрос × движок» одного замера.
 *
 * Матрица читала ту же выборку ответов, что и живая цитата, и просила у неё 500
 * штук. Потолок страницы — 200, и договор прямо говорит, что больший запрос
 * **обрезается, а не отвергается**. За остатком никто не шёл, поэтому при
 * восьми движках и двух прогонах на вопрос (16 ответов) до матрицы доезжало
 * около двенадцати вопросов из пятидесяти строк, а остальные тридцать восемь
 * рисовались штриховкой «не измерено» — при том, что замер по ним прошёл.
 * Экран врал молча и ровно там, где клиент ищет свои дыры.
 *
 * Второе враньё было тише: выборка не привязана к прогону вовсе, поэтому в одну
 * клетку складывались попытки РАЗНЫХ замеров. Клетка «названы в обоих прогонах»
 * могла оказаться собранной из четырёх попыток двух недель, а «в одном из двух»
 * — из одной попытки позапрошлой.
 *
 * Поэтому клетки считаются здесь, отдельно от цитаты, и по одному замеру:
 * страницы дочитываются до конца, а `run_id` держит попытки внутри своего
 * прогона (`runs_per_cell` = 2 по схеме, значит `tries` больше двух не бывает).
 * Сколько страниц понадобится, известно заранее из `planned_tasks` самого
 * замера, так что бесконечного дочитывания здесь нет: `MAX_PAGES` — это
 * предохранитель на случай ядра, которое одним экраном уже не показать, и о
 * достигнутом потолке экран обязан сказать словами, а не молча укоротить
 * таблицу.
 */

import { useEffect, useRef, useState } from 'react';

import { collator } from '@shared/src/intl';

import { requestWith } from '../api/client';
import { useRead, type Page } from './query';
import type { components } from '../api/schema';

type Answer = components['schemas']['Answer'];
type Run = components['schemas']['Run'];
type Sentiment = components['schemas']['Sentiment'];
type ParserKind = components['schemas']['ParserKind'];

/** Размер страницы. Ровно потолок сервера: больший запрос он обрежет молча. */
const PAGE = 200;

/**
 * Предохранитель дочитывания. Ядро в тысячу вопросов на девяти движках — это
 * 18 000 ответов и 90 страниц, столько экран не тянет и показывать столько
 * строк всё равно нельзя. Двадцать пять страниц закрывают ядро примерно на
 * триста вопросов целиком, а всё, что больше, честнее показать частью с
 * подписью, чем ждать минуту.
 */
const MAX_PAGES = 25;

/**
 * Одна попытка клетки: то, чем клетка объясняет свой цвет.
 *
 * Цвет — это итог двух попыток, а вопрос человека к цветной клетке всегда про
 * одну: где именно нас назвали и на каком месте. Поэтому попытки не
 * сворачиваются в счётчики при чтении: `tries`/`hits` держат состояние, а этот
 * список — объяснение.
 */
export interface CellTry {
  id: string;
  /**
   * Номер прогона в клетке, 1..`runs_per_cell`. `null` — сервер номера не дал:
   * поле договором не обязано, а порядок чтения номером не является. Выдумать
   * его нельзя — «Прогон 1 из 2» на неизвестном номере это утверждение, а не
   * подстановка по умолчанию.
   */
  no: number | null;
  at: string;
  mentioned: boolean;
  position: number | null;
  sentiment: Sentiment | null;
  /** Чем прочитан ответ: у запасного разбора места в списке не бывает. */
  parser: ParserKind | null;
  snippet: string | null;
  model: string | null;
}

/** Попытки и попадания одной клетки внутри замера. */
export interface Cell {
  tries: number;
  hits: number;
  attempts: CellTry[];
}

/** Строка матрицы: вопрос и его клетки по кодам движков. */
export interface CellRow {
  id: string;
  text: string;
  cells: Map<string, Cell>;
}

export interface Cells {
  /** Замер, по которому построена матрица. `null` — завершённых замеров нет. */
  run: Run | null;
  rows: CellRow[];
  /** Прочитан ли замер до конца. `false` — сработал предохранитель страниц. */
  complete: boolean;
}

export type CellsReading =
  | { kind: 'loading' }
  | { kind: 'ready'; data: Cells }
  | { kind: 'failed'; error: unknown };

/**
 * Клетки последнего завершённого недельного замера бренда.
 *
 * Вид замера задан жёстко. Пульс — это выборочная сетка, а не перепись, и его
 * клетки в матрице встали бы вперемешку с полными; зонд памяти спрашивает те же
 * вопросы с выключенным поиском, и договор по нему отдаёт пустую страницу
 * ответов вовсе.
 */
export function useCells(brandId: string): CellsReading {
  const runs = useRead<Page<Run>>('/runs', {
    brand_id: brandId,
    kind: 'weekly',
    state: 'completed',
    limit: 1,
  });

  const run = runs.kind === 'ready' ? (runs.data.items[0] ?? null) : null;
  const runId = run?.id ?? null;
  // Сколько страниц нужно, известно из самого замера: `planned_tasks` — это
  // вопросы × движки × прогоны. Ноль означает, что счётчик не заполнен, и тогда
  // идём до конца курсора, но не дальше предохранителя.
  const planned = run?.planned_tasks ?? 0;

  const [held, setHeld] = useState<{ of: string; state: Held }>({
    of: '',
    state: { kind: 'loading' },
  });

  // Дочитывание не переживает смену бренда: страницы прошлого замера, пришедшие
  // после переключения, легли бы в матрицу нового.
  const reading = useRef<AbortController | null>(null);
  useEffect(() => () => reading.current?.abort(), []);

  const key = `${brandId}|${runId ?? ''}`;

  useEffect(() => {
    if (!runId) return;

    const controller = new AbortController();
    reading.current?.abort();
    reading.current = controller;

    const wanted =
      planned > 0 ? Math.min(Math.ceil(planned / PAGE), MAX_PAGES) : MAX_PAGES;

    const read = async () => {
      const items: Answer[] = [];
      // Уже пройденные курсоры. Повтор курсора означает, что сервер отдаёт ту же
      // страницу снова: без этой памяти чтение крутило бы её до предохранителя,
      // складывая одни и те же ответы в клетки (`tries` вырастает выше числа
      // прогонов) и теряя остаток замера. Останавливаемся и говорим словами, что
      // замер прочитан не полностью.
      const seen = new Set<string>();
      let cursor: string | null = null;
      let pages = 0;
      let drained = false;
      while (pages < wanted) {
        const { body }: { body: Page<Answer> } = await requestWith<Page<Answer>>(
          `/brands/${brandId}/answers`,
          {
            query: {
              run_id: runId,
              organic: true,
              limit: PAGE,
              ...(cursor ? { cursor } : {}),
            },
            signal: controller.signal,
          },
        );
        items.push(...body.items);
        pages += 1;
        if (cursor) seen.add(cursor);
        cursor = body.next_cursor;
        if (!cursor) {
          drained = true;
          break;
        }
        if (seen.has(cursor)) break;
      }
      return { items, complete: drained };
    };

    read()
      .then(({ items, complete }) => {
        if (controller.signal.aborted) return;
        setHeld({ of: key, state: { kind: 'ready', rows: rowsOf(items), complete } });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setHeld({ of: key, state: { kind: 'failed', error } });
      });

    return () => controller.abort();
  }, [key, runId, brandId, planned]);

  // Состояние вычисляется при отрисовке, а не выправляется эффектом, — тот же
  // приём, что в `useRead`: иначе кадр показывал бы клетки прошлого бренда под
  // подписью нового.
  if (runs.kind === 'failed') return { kind: 'failed', error: runs.error };
  if (runs.kind === 'loading') return { kind: 'loading' };
  if (!run) return { kind: 'ready', data: { run: null, rows: [], complete: true } };
  if (held.of !== key || held.state.kind === 'loading') return { kind: 'loading' };
  if (held.state.kind === 'failed') return { kind: 'failed', error: held.state.error };
  return {
    kind: 'ready',
    data: { run, rows: held.state.rows, complete: held.state.complete },
  };
}

/** Прочитанные страницы замера. Сам замер сюда не кладётся: он есть при отрисовке. */
type Held =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: CellRow[]; complete: boolean }
  | { kind: 'failed'; error: unknown };

/**
 * Ответы — в строки матрицы.
 *
 * Порядок строк — от худших: вопрос, по которому не назвал никто, стоит выше
 * вопроса, где назвали все, потому что экран открывают ради первых. Внутри
 * равной доли порядок задаёт текст вопроса, иначе строки прыгали бы между
 * перерисовками при равных числах.
 */
function rowsOf(items: Answer[]): CellRow[] {
  const byQuestion = new Map<string, CellRow>();
  for (const answer of items) {
    if (!answer.is_organic) continue;
    const row = byQuestion.get(answer.question_id) ?? {
      id: answer.question_id,
      text: answer.question_text,
      cells: new Map<string, Cell>(),
    };
    const cell = row.cells.get(answer.engine_code) ?? {
      tries: 0,
      hits: 0,
      attempts: [],
    };
    cell.tries += 1;
    if (answer.brand_mentioned) cell.hits += 1;
    cell.attempts.push({
      id: answer.id,
      no: answer.attempt_no ?? null,
      at: answer.fetched_at,
      mentioned: answer.brand_mentioned,
      position: answer.brand_position ?? null,
      sentiment: answer.brand_sentiment ?? null,
      parser: answer.parser ?? null,
      snippet: answer.snippet ?? null,
      model: answer.model_version ?? null,
    });
    // Страницы приходят от свежих к старым, и внутри клетки прогоны вставали бы
    // вторым-первым. Порядок прогонов — это порядок чтения окна: «в первом
    // назвали, во втором нет» и «в первом нет, во втором назвали» читаются
    // по-разному, хотя клетка у них одна. Безномерные попытки идут в конец и
    // между собой сохраняют порядок чтения: сортировка в JS устойчива.
    cell.attempts.sort((left, right) => (left.no ?? Infinity) - (right.no ?? Infinity));
    row.cells.set(answer.engine_code, cell);
    byQuestion.set(answer.question_id, row);
  }

  return [...byQuestion.values()].sort((left, right) => {
    const byShare = shareOf(left) - shareOf(right);
    // Сортировка текста вопроса — по алфавиту активной локали, а не по 'ru'
    // буквой: в арабском алфавит другой, а порядок строк в матрице человек
    // читает как утверждение о данных.
    return byShare !== 0 ? byShare : collator().compare(left.text, right.text);
  });
}

/** Доля попаданий вопроса по всем его клеткам: 0 — не назвал никто. */
function shareOf(row: CellRow): number {
  let tries = 0;
  let hits = 0;
  for (const cell of row.cells.values()) {
    tries += cell.tries;
    hits += cell.hits;
  }
  return tries === 0 ? 0 : hits / tries;
}
